import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from '../../ui/notify'
import { appUrl, failed } from '../parts'
import {
  appBackupQuery,
  backupOf,
  backupStatusQuery,
  isBackupDetail,
  saveBackupThreshold,
  type AppBackupSummary,
  type BackupComponent,
  type BackupStatusPage,
} from './backupApi'
import '../../inventoree/UnassignedBand.css'
import './Backup.css'

/**
 * BACKUP-UL PE APLICATIE, pe ecran - `v4/api/src/BusinessApps/Backup/AppBackups.php`.
 *
 *  - `BackupMark`          semnul din lista, langa aplicatie (semnalul in tooltip);
 *  - `BackupBand`          banda de AVERTISMENT din capul listei: aplicatiile
 *                          fara copie recenta, cu ultima rulare picata, sau
 *                          fara nicio sursa de backup;
 *  - `BackupCard`          cardul "Backup" de pe fisa: fiecare server, ultima
 *                          copie buna, de unde se stie, si pragul aplicatiei;
 *  - `BackupDetailBand`    banda galbena de pe fisa, la fel ca cea din
 *                          lista - doar avertisment, NU se numara la lipsuri
 *                          si nu opreste activarea.
 *
 * "Backup unknown" NU inseamna ca lipseste copia: nicio sursa nu spune nimic
 * despre server. Cardul spune ce trebuie conectat, nu ca lipseste ceva.
 *
 * Un server mai vechi poate raspunde pe aceeasi cale cu altceva: fara forma
 * asteptata (`isBackupDetail`, `backupOf`), nu se deseneaza nimic.
 */

const NEEDS_LOOK: Record<string, number> = { overdue: 0, warning: 1, unknown: 2 }

const ICON: Record<string, string> = {
  ok: 'fa-check-circle',
  warning: 'fa-exclamation-triangle',
  overdue: 'fa-exclamation-circle',
  unknown: 'fa-question-circle',
}

const NO_BLOCK = 'Warning only - it does not block activation.'

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

export function ago(hours: number | null): string {
  if (hours === null) return ''
  if (hours < 1) return 'less than an hour ago'
  if (hours < 48) return `${plural(Math.round(hours), 'hour', 'hours')} ago`
  return `${plural(Math.round(hours / 24), 'day', 'days')} ago`
}

function statusApps(data: BackupStatusPage | undefined): AppBackupSummary[] {
  if (!data || data.ready !== true || data.apps == null || typeof data.apps !== 'object' || Array.isArray(data.apps)) return []
  return Object.values(data.apps).filter((b): b is AppBackupSummary => b != null && typeof b.state === 'string')
}

/** Semnul din lista: starea backup-ului aplicatiei, cu semnalele in tooltip. */
export function BackupMark({ appId }: { appId: number }) {
  const status = useQuery(backupStatusQuery())
  const b = backupOf(status.data, appId)
  if (b === null || b.state === 'none') return null
  const signals = Array.isArray(b.signals) ? b.signals : []

  return (
    <span className={`app-backup-mark is-${b.state}`} title={signals.length > 0 ? signals.join('\n') : b.label}>
      <i className={`fas ${ICON[b.state] ?? 'fa-database'}`} aria-hidden="true"></i> {b.label}
    </span>
  )
}

/** Banda din capul listei: ce aplicatii au backup-ul de verificat. */
export function BackupBand() {
  const [open, setOpen] = useState(false)
  const status = useQuery(backupStatusQuery())
  const list = statusApps(status.data)
    .filter((b) => b.state in NEEDS_LOOK)
    .sort((a, b) => NEEDS_LOOK[a.state] - NEEDS_LOOK[b.state] || b.criticality - a.criticality || a.name.localeCompare(b.name))
  if (list.length === 0) return null

  const overdue = list.filter((b) => b.state === 'overdue').length
  const failedRun = list.filter((b) => b.state === 'warning').length
  const unknown = list.filter((b) => b.state === 'unknown').length
  const parts: string[] = []
  if (overdue > 0) parts.push(`${plural(overdue, 'application', 'applications')} without a recent backup`)
  if (failedRun > 0) parts.push(overdue > 0 ? `${failedRun} with a failed last run` : `${plural(failedRun, 'application', 'applications')} with a failed last backup run`)
  if (unknown > 0) parts.push(parts.length > 0 ? `${unknown} with no backup data` : `${plural(unknown, 'application', 'applications')} with no backup data`)
  const [head, ...rest] = parts

  return (
    <div className={`od-unknown-band app-backup-band${open ? ' is-open' : ''}`} id="appsBackupBand">
      <button type="button" className="od-unknown-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <i className="fas fa-database" aria-hidden="true"></i>
        <strong className="od-unknown-count">{head}</strong>
        {rest.map((r) => (
          <span key={r} className="od-unknown-last">
            {r}
          </span>
        ))}
        <span className="od-unknown-last">{NO_BLOCK}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} od-unknown-chevron`} aria-hidden="true"></i>
      </button>
      {open ? (
        <div className="od-unknown-body">
          <table className="table table-condensed od-unknown-table">
            <thead>
              <tr>
                <th>Application</th>
                <th>Backup</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {list.map((b) => (
                <tr key={b.appId} data-id={b.appId} className={`is-${b.state}`}>
                  <td>
                    <a href={`${appUrl(b.appId)}#appBackup`} target="_blank" rel="noopener noreferrer">{b.name}</a>
                  </td>
                  <td className="adm-nowrap">{b.label}</td>
                  <td>{(b.signals ?? []).join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  )
}

/** Cardul "Backup" de pe fisa aplicatiei. */
export function BackupCard({ appId }: { appId: number }) {
  const query = useQueryClient()
  const detail = useQuery(appBackupQuery(appId))
  const [editing, setEditing] = useState(false)
  const [hours, setHours] = useState('')
  const [busy, setBusy] = useState(false)
  const b = detail.data
  if (!isBackupDetail(b)) return null

  const startEdit = () => {
    setHours(b.thresholdDefault ? '' : String(b.thresholdHours))
    setEditing(true)
  }
  const save = () => {
    setBusy(true)
    saveBackupThreshold(appId, hours.trim())
      .then((r) => {
        toast(r.message, 'Backup', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') {
          setEditing(false)
          void query.invalidateQueries({ queryKey: ['business-apps', 'backup'] })
        }
      })
      .catch((e) => toast(failed(e), 'Backup', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <>
      <div className="adm-section" id="app-sec-backup">
        <h4>Backup</h4>
        <span className="adm-section-note">
          last good copy of every server · threshold {b.thresholdLabel}
          {b.thresholdDefault ? ' (default)' : ''}
        </span>
        {b.canEdit && !editing && (
          <button type="button" id="appBackupEdit" className="btn btn-default btn-xs" style={{ marginLeft: 'auto' }} onClick={startEdit}>
            <i className="fa fa-clock" aria-hidden="true"></i> Threshold
          </button>
        )}
      </div>
      <div className={`adm-card app-backup is-${b.state}`} id="appBackup">
        {editing && (
          <div className="app-backup-threshold">
            <label htmlFor="appBackupHours">Alert when the last good copy is older than</label>
            <input
              id="appBackupHours"
              type="number"
              min={1}
              className="form-control input-sm"
              value={hours}
              placeholder={String(b.defaultHours)}
              onChange={(ev) => setHours(ev.target.value)}
            />
            <span className="adm-muted small">hours (empty = default, {b.defaultHours} for this criticality)</span>
            <button type="button" className="btn btn-primary btn-xs" disabled={busy} onClick={save}>
              Save
            </button>
            <button type="button" className="btn btn-default btn-xs" disabled={busy} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        )}
        {b.components.length === 0 ? (
          <p className="text-muted" style={{ padding: '10px 15px' }}>
            No servers on this application yet: add them under Components to follow their backups.
          </p>
        ) : (
          <ul className="app-backup-lines">
            {b.components.map((c, i) => (
              <BackupLine key={`${c.deviceId ?? 'ci'}-${c.ciId ?? i}`} item={c} />
            ))}
          </ul>
        )}
      </div>
    </>
  )
}

function BackupLine({ item }: { item: BackupComponent }) {
  /* Un server care nu trebuie sa aiba copie (web, statie) si nu spune nimic: nu e rosu. */
  const untracked = item.state === 'unknown' && !item.required
  const state = untracked ? 'untracked' : item.state

  return (
    <li className={`app-backup-line is-${state}`}>
      <div className="app-backup-head">
        <strong>{item.name}</strong> <span className="adm-muted small">{item.roleLabel}</span>
        <span className={`app-backup-state is-${state}`}>
          <i className={`fas ${untracked ? 'fa-minus-circle' : (ICON[item.state] ?? 'fa-database')}`} aria-hidden="true"></i>{' '}
          {untracked ? 'Not tracked' : item.stateLabel}
        </span>
      </div>
      {item.lastOk !== null ? (
        <div className="app-backup-when">
          Last good copy {item.lastOk}
          {item.ageHours !== null ? ` (${ago(item.ageHours)})` : ''}
          {item.source ? <span className="adm-muted"> · {item.source}</span> : null}
        </div>
      ) : null}
      {item.state !== 'ok' && item.message !== '' ? <div className="app-backup-msg">{item.message}</div> : null}
    </li>
  )
}

/** Titlul benzii de pe fisa, dupa stare - aceleasi cuvinte ca banda din lista. */
const DETAIL_HEAD: Record<string, string> = {
  overdue: 'No recent backup for this application',
  warning: 'The last backup run failed for this application',
  unknown: 'No backup data for this application',
}

/**
 * Banda de backup de pe fisa: ACELASI design ca `BackupBand` din lista (banda
 * galbena, `od-unknown-band app-backup-band`), cu serverele la deschidere.
 * Avertisment, nu lipsa: nu opreste activarea. Backup in regula = nu apare.
 */
export function BackupDetailBand({ appId }: { appId: number }) {
  const [open, setOpen] = useState(false)
  const detail = useQuery(appBackupQuery(appId))
  const b = detail.data
  if (!isBackupDetail(b) || !(b.state in NEEDS_LOOK)) return null
  const servers = b.components.filter((c) => c.state !== 'ok' && !(c.state === 'unknown' && !c.required))

  return (
    <div className={`od-unknown-band app-backup-band is-detail${open ? ' is-open' : ''}`} id="appBackupBand">
      <button type="button" className="od-unknown-head" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <i className="fas fa-database" aria-hidden="true"></i>
        <strong className="od-unknown-count">{DETAIL_HEAD[b.state] ?? b.label}</strong>
        <span className="od-unknown-last">{NO_BLOCK}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} od-unknown-chevron`} aria-hidden="true"></i>
      </button>
      {open ? (
        <div className="od-unknown-body">
          {servers.length === 0 ? (
            <p className="od-unknown-empty">{(Array.isArray(b.signals) ? b.signals : []).join(' · ') || b.label}</p>
          ) : (
            <table className="table table-condensed od-unknown-table">
              <thead>
                <tr>
                  <th>Server</th>
                  <th>Backup</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {servers.map((c, i) => (
                  <tr key={`${c.deviceId ?? 'ci'}-${c.ciId ?? i}`} className={`is-${c.state}`}>
                    <td>
                      <a href="#app-sec-backup">{c.name}</a> <span className="adm-muted small">{c.roleLabel}</span>
                    </td>
                    <td className="adm-nowrap">{c.stateLabel}</td>
                    <td>{c.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
    </div>
  )
}
